<template>
	<div class="postCreation">
		<FieldLayout title="제목">
			<InputText
				v-model="title"
				maxlength="30"
				:placeholder="PLACEHOLDER_TITLE"
				:transparent="true"
				@input="onChangeLength"
			/>
		</FieldLayout>
		<div class="text-editor">
			<span class="editor_title">내용</span>
			<Ckeditor v-model="content" :editor="ckEditor" :config="editorConfig" :autogrow="true" />
		</div>
		<FieldLayout title="대표 이미지">
			<div class="img_info">
				<label for="file">
					<div v-if="thumbnail" class="btn-upload_img">
						<img :src="thumbnail?.src" alt="이미지 아이콘" loading="lazy" />
					</div>
					<div v-else class="btn-upload"><AddIcon /></div>
				</label>
				<input id="file" type="file" name="file" accept="image/*" hidden @change="getImage($event)" />
				<p @click="console.log(title, content, fileImages)">※용량 제한 500KB, 1장만 업로드 가능</p>
			</div>
		</FieldLayout>

		<div class="button_box">
			<button class="c_button mid" @click="$router.replace({ query: { modal: 'close' } })">취소</button>
			<button class="c_button primary-fill" :disabled="!title || !content" @click="onClickConfirm">
				{{ props.data ? '저장' : '완료' }}
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
// lib
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
// editor
import { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';

// api
import { Knowhow, useSaveKnowhowApi } from '@/api/proTips';
// asset
import AddIcon from '@/assets/img/proTips/add.svg?component';
// component
import InputText from '@/components/atom/input/InputText.vue';
import FieldLayout from '@/components/form/FieldLayout.vue';
// constant
import {
	COMPANY_TITLE,
	CONFIRM,
	CONFIRM_EDIT_ITEM,
	CONFIRM_REGISTER_POST,
	MESSAGE_EDIT_COMPLETED,
	MESSAGE_REGISTRATION_COMPLETED,
	PLACEHOLDER_TITLE,
} from '@/constants/title';
import { editorConfig } from '@/plugins/editorConfig';
// store
import { usePopupStore } from '@/stores/popup';
import { useToastStore } from '@/stores/toast';
// util
import useSelectImage from '@/utils/directive/composables/useSelectImage';
// type
interface ThumbnailsProps {
	src: string;
	key?: number;
	name?: string;
}
// props
const props = defineProps<{
	data?: Knowhow;
}>();

// -----variable-----
const router = useRouter();
const popupStore = usePopupStore();
const toastStore = useToastStore();

const title = ref('');
const content = ref('');
const fileImages = ref();
const existingPostPk = ref();
const existingFilePk = ref();
const thumbnail = ref<ThumbnailsProps>();
const ckEditor = ClassicEditor;

const { getImage } = useSelectImage({ image: fileImages, thumbnail: thumbnail });

// -----api-----
const { mutateAsync: saveKnowhow } = useSaveKnowhowApi();

// -----function-----
const onClickConfirm = () => {
	popupStore.openPopup({
		content: COMPANY_TITLE,
		subtext: props.data ? CONFIRM_EDIT_ITEM : CONFIRM_REGISTER_POST,
		confirm: async () => {
			try {
				const formData = new FormData();
				formData.append('title', title.value);
				formData.append('content', content.value);
				if (fileImages.value) {
					formData.append('image', fileImages.value);
					if (props.data && props.data.file_pk) {
						formData.append('del_image_pk', existingFilePk.value);
					}
				}
				if (props.data) {
					formData.append('pk', existingPostPk.value);
				}
				await saveKnowhow(formData);

				toastStore.openToast({
					subContent: COMPANY_TITLE,
					content: props.data ? MESSAGE_EDIT_COMPLETED : MESSAGE_REGISTRATION_COMPLETED,
				});
				router.replace({ query: { modal: 'close' } });
			} catch (error) {
				console.log(error);
			}
		},
		confirmText: CONFIRM,
	});
};

const onChangeLength = (e: Event) => {
	const MAX_LENGTH = 30;
	const target = (e.target as HTMLInputElement).value;
	if (target.length > MAX_LENGTH) {
		title.value = target.substring(0, MAX_LENGTH);
		(e.target as HTMLInputElement).value = target.substring(0, MAX_LENGTH);
	}
};

// -----composition-----
onMounted(() => {
	if (props.data) {
		title.value = props.data.knowhow_title;
		content.value = props.data.knowhow_content;
		existingPostPk.value = props.data.knowhow_pk;
		if (props.data.file_pk && props.data.file_path) {
			thumbnail.value = { src: props.data.file_path };
			existingFilePk.value = props.data.file_pk;
		}
	}
});
</script>

<style scoped lang="scss">
.postCreation {
	display: flex;
	flex-direction: column;
	gap: 3.2rem;
	.btn-upload {
		width: 10.5rem;
		height: 10.5rem;
		display: flex;
		justify-content: center;
		align-items: center;
		border-radius: 0.8rem;
		background-color: var(--color-base-light);
		cursor: pointer;
	}
	.btn-upload_img {
		img {
			width: 10.5rem;
			height: 10.5rem;
			border-radius: 0.8rem;
		}
	}
	.img_info {
		display: flex;
		flex-direction: column;
		gap: 0.6rem;
		color: var(--color-base-light);
		font-weight: var(--font-w-regular);
		font-size: 1.4rem;
		line-height: 1.96rem;

		label {
			width: fit-content;
		}
	}
	.button_box {
		display: flex;
		gap: 1rem;
		color: var(--color-base-white);
		font-weight: var(--font-w-medium);
		font-size: var(--font-s-regular);
		line-height: 2.1rem;
		button {
			flex: 1;
			height: 5.2rem;
		}
	}
}
.FieldLayout {
	color: var(--color-base-white);
	font-weight: var(--font-w-medium);
	font-size: var(--font-s-mid);
	line-height: 1.79rem;
}
</style>
